import LineChart2 from "@/components/LineChart2.js";
import ThiSensorService from "@/services/ThiSensorService";

function buildThiRangeAnnotations() {
  return [
    {
      class: "thi-range",
      drawTime: "beforeDatasetsDraw",
      type: "box",
      mode: "horizontal",
      yScaleID: "y-axis-0",
      yMax: 68,
      backgroundColor: "rgba(0, 206, 95, 0.25)",
      borderWidth: 1,
      borderColor: "rgba(0, 0, 0, 0)"
    },
    {
      class: "thi-range",
      drawTime: "beforeDatasetsDraw",
      type: "box",
      mode: "horizontal",
      yScaleID: "y-axis-0",
      yMin: 68,
      yMax: 72,
      backgroundColor: "rgba(156, 187, 0, 0.25)",
      borderWidth: 1,
      borderColor: "rgba(0, 0, 0, 0)"
    },
    {
      class: "thi-range",
      drawTime: "beforeDatasetsDraw",
      type: "box",
      mode: "horizontal",
      yScaleID: "y-axis-0",
      yMin: 72,
      yMax: 80,
      backgroundColor: "rgba(255, 109, 52, 0.25)",
      borderWidth: 1,
      borderColor: "rgba(0, 0, 0, 0)"
    },
    {
      class: "thi-range",
      drawTime: "beforeDatasetsDraw",
      type: "box",
      mode: "horizontal",
      yScaleID: "y-axis-0",
      yMin: 80,
      backgroundColor: "rgba(161, 0, 7, 0.15)",
      borderWidth: 1,
      borderColor: "rgba(0, 0, 0, 0)"
    }
  ];
}

function buildYAxisTicks(yMin, yMax) {
  const ticks = {};
  if (yMin !== null && yMin !== undefined) ticks.min = yMin;
  if (yMax !== null && yMax !== undefined) ticks.max = yMax;
  return ticks;
}

function buildLineChartOptions(yMin, yMax, annotations) {
  return {
    animation: {
      duration: 0
    },
    hover: {
      animationDuration: 0,
      mode: "x"
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [
        {
          id: "x-axis-0",
          distribution: "linear",
          type: "time",
          time: {
            unit: "hour",
            displayFormats: {
              minute: "MMM D. H:mm",
              hour: "MMM D. H[h]",
              day: "MMM D."
            }
          },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 20
          }
        }
      ],
      yAxes: [
        {
          id: "y-axis-0",
          ticks: buildYAxisTicks(yMin, yMax)
        }
      ]
    },
    annotation: {
      annotations: annotations || []
    }
  };
}

export default {
  name: "ThiCharts",
  components: { LineChart2 },
  props: {
    thi_sensor_id: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loaded: false,
      fromDate: this.$moment().subtract(4, "days").set("hour", 0).set("minute", 0).set("second", 0),
      toDate: this.$moment().add(1, "days").set("hour", 0).set("minute", 0).set("second", 0),
      tempCollection: { datasets: [] },
      humidityCollection: { datasets: [] },
      thiCollection: { datasets: [] },
      tempChartOptions: buildLineChartOptions(null, null),
      humidityChartOptions: buildLineChartOptions(0, 100),
      thiChartOptions: buildLineChartOptions(55, 90, buildThiRangeAnnotations())
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    formatDate(date) {
      return this.$moment(date).format("YYYY-MM-DDTHH:mm:ss");
    },
    hasData(collection) {
      return (
        collection &&
        collection.datasets &&
        collection.datasets[0] &&
        collection.datasets[0].data.length > 0
      );
    },
    buildCollection(series, key, label, backgroundColor) {
      const data = (series || []).map(point => ({
        x: point.time,
        y: point[key]
      }));

      return {
        datasets: [
          {
            label,
            data,
            backgroundColor,
            fill: "false",
            lineTension: 0
          }
        ]
      };
    },
    updateChartOptions() {
      const chartOptions = [
        this.tempChartOptions,
        this.humidityChartOptions,
        this.thiChartOptions
      ];
      const boxes = this.createDayNightAnnotations();

      // Chart.js + moment-timezone crash if ticks.min/max are moment objects
      // ("e._z.utcOffset is not a function"). Pass ISO strings instead.
      const from = this.$moment(this.fromDate).toISOString();
      const to = this.$moment(this.toDate).toISOString();

      chartOptions.forEach(options => {
        options.scales.xAxes[0].ticks.min = from;
        options.scales.xAxes[0].ticks.max = to;
        options.annotation.annotations = options.annotation.annotations
          .filter(a => a.class !== "day-night")
          .concat(boxes);
      });
    },
    createDayNightAnnotations() {
      const boxes = [];
      const template = {
        class: "day-night",
        drawTime: "beforeDatasetsDraw",
        type: "box",
        mode: "horizontal",
        xScaleID: "x-axis-0",
        xMin: null,
        xMax: null,
        backgroundColor: "rgba(50,50,50,.1)",
        borderWidth: 1,
        borderColor: "rgba(0, 0, 0, 0)"
      };

      let from = this.$moment(this.fromDate);
      const to = this.$moment(this.toDate);
      const hours = from.hours();

      if (hours < 6 && hours >= 0) {
        template.xMin = from.toISOString();
        template.xMax = from.hours(6).toISOString();
      } else if (hours > 6 && hours < 18) {
        template.xMin = from.hours(18).toISOString();
        template.xMax = from
          .add(1, "days")
          .hours(6)
          .toISOString();
      } else {
        template.xMin = from.toISOString();
        template.xMax = from
          .add(1, "days")
          .hours(6)
          .toISOString();
      }
      boxes.push(JSON.parse(JSON.stringify(template)));

      template.xMin = this.$moment(template.xMin)
        .hours(18)
        .add(-1, "days")
        .toISOString();
      while (this.$moment(template.xMax).isBefore(to)) {
        template.xMin = this.$moment(template.xMin)
          .add(1, "days")
          .toISOString();
        template.xMax = this.$moment(template.xMax)
          .add(1, "days")
          .toISOString();
        boxes.push(JSON.parse(JSON.stringify(template)));
      }

      return boxes;
    },
    stepDateFilters(direction) {
      const diff = this.$moment(this.toDate).diff(this.$moment(this.fromDate));

      if (direction === "back") {
        this.fromDate = this.$moment(this.fromDate).subtract(diff, "milliseconds");
        this.toDate = this.$moment(this.toDate).subtract(diff, "milliseconds");
      } else if (direction === "forward") {
        this.fromDate = this.$moment(this.fromDate).add(diff, "milliseconds");
        this.toDate = this.$moment(this.toDate).add(diff, "milliseconds");
      }

      this.fetchData();
    },
    async fetchData() {
      this.loaded = false;
      const from = this.formatDate(this.fromDate);
      const to = this.formatDate(this.toDate);

      try {
        const [tempRes, humRes, thiRes] = await Promise.all([
          ThiSensorService.tempData(this.thi_sensor_id, from, to),
          ThiSensorService.humidityData(this.thi_sensor_id, from, to),
          ThiSensorService.thiData(this.thi_sensor_id, from, to)
        ]);

        this.tempCollection = this.buildCollection(
          tempRes && tempRes.data && tempRes.data.temp,
          "temp",
          this.$t("thiSensorGenerics.temp"),
          "rgba(17,62,83,0.3)"
        );
        this.humidityCollection = this.buildCollection(
          humRes && humRes.data && humRes.data.hum,
          "hum",
          this.$t("thiSensorGenerics.humidity"),
          "rgba(42,141,244,0.3)"
        );
        this.thiCollection = this.buildCollection(
          thiRes && thiRes.data && thiRes.data.thi,
          "thi",
          this.$t("thiSensorGenerics.thi"),
          "rgba(42,196,106,0.3)"
        );

        this.updateChartOptions();
      } catch (e) {
        this.tempCollection = { datasets: [] };
        this.humidityCollection = { datasets: [] };
        this.thiCollection = { datasets: [] };
      } finally {
        this.loaded = true;
      }
    }
  },
  watch: {
    thi_sensor_id() {
      this.fetchData();
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/_thi-sensor-profile/thi-charts/thi-charts.js